import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useRouter } from 'expo-router';

import { toaster } from 'components/toaster/toaster';
import { useExposeSignupFlowRedesignExperiment } from 'experiments/signup-flow-redesign/use-enable-signup-flow-redesign-experiment';
import useErrorModal from 'hooks/use-error-modal';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import {
  AuthInterface,
  ModalAuthScreen,
  ModalAuthState,
  ModalAuthTrigger,
  OpenSignInModalProps,
} from 'state/auth/types';
import { t } from 'state/intl/translations';
import { APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS } from 'utils/constants';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { TraceName, startTrace } from 'utils/performance/trace';

import { useAccountAuthentication } from './hooks/use-account-authentication';
import { UserDetails, _useCurrentUser } from './hooks/use-current-user';

// Initial state for the modal authentication hook.
// This defines the default values for all modal properties when the component mounts or when the modal is explicitly closed.
const initialModalAuthState: ModalAuthState = {
  screen: ModalAuthScreen.CLOSED,
  shouldDisplaySignInSuccessMessage: true,
  shouldDisableHeadlineLinks: false,
  shouldSkipDefaultSignInCallback: false,
  shouldWaitLoginForVendor: false,
  disableEmailInput: false,
  loginForVendorIsLoading: false,
  trigger: ModalAuthTrigger.Global,
  onClose: undefined,
  onPreSignIn: undefined,
  onSignInSuccess: undefined,
  user: undefined,
  providerType: undefined,
  errorMessage: '',
  heading: '',
};

export const AuthContext = createContext<AuthInterface>(({} as any) as AuthInterface);
export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [modalAuthState, setModalAuthState] = useState<ModalAuthState>(initialModalAuthState);
  const [isReAuthenticating, setIsReAuthenticating] = useState(false);
  const [otpAuthError, setOtpAuthError] = useState(null);
  const modalAuthIsOpen = modalAuthState.screen !== ModalAuthScreen.CLOSED;
  const [accountCallbackUrl, setAccountCallbackUrl] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const router = useRouter();

  useExposeSignupFlowRedesignExperiment(modalAuthState.screen === ModalAuthScreen.SIGN_UP);

  /**
   * A handler function to update the modal authentication state.
   * It merges the new state with the previous state, ensuring that no data is lost
   * and only specified properties are updated.
   *
   * @param {Partial<ModalAuthState>} newState - The new state to be merged with the previous state.
   */
  const handleSetModalAuthState = useCallback((newState: Partial<ModalAuthState>) => {
    setModalAuthState(prevModalAuthState => {
      const mergedState = { ...prevModalAuthState, ...newState } as ModalAuthState;

      const prevUser = prevModalAuthState.user || {};
      const newUser = newState.user || {};
      mergedState.user = {
        name: newUser.name ?? prevUser.name,
        email: newUser.email ?? prevUser.email,
      };

      if (JSON.stringify(prevModalAuthState) === JSON.stringify(mergedState)) {
        return prevModalAuthState;
      }

      if (newState.screen && prevModalAuthState.screen !== newState.screen) {
        logRBIEvent({
          name: CustomEventNames.AUTH_DRAWER_VIEW,
          type: EventTypes.Other,
          attributes: {
            screen: newState.screen,
          },
        });
      }
      return mergedState;
    });
  }, []);

  const openSignInModal = useCallback(
    (params?: OpenSignInModalProps) => {
      startTrace(TraceName.AUTH_MODAL_LOAD);
      const {
        errorMessage = '',
        shouldDisableHeadlineLinks = false,
        shouldWaitLoginForVendor = false,
        navigateOnSuccessRoute,
        trigger = ModalAuthTrigger.Global,
        onClose,
      } = params || {};

      handleSetModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        shouldDisplaySignInSuccessMessage: true,
        navigateOnSuccessRoute,
        errorMessage,
        shouldDisableHeadlineLinks,
        shouldWaitLoginForVendor,
        trigger,
        // Only set onClose if it's provided to avoid overriding the default close handler
        ...(onClose ? { onClose } : {}),
      });
    },
    [handleSetModalAuthState]
  );

  const onConfirmErrorDialog = useCallback(() => {
    openSignInModal();
  }, [openSignInModal]);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    onConfirm: onConfirmErrorDialog,
    modalAppearanceEventMessage: 'Error: Auth Error',
  });

  const {
    refetchCurrentUser,
    refreshCurrentUser,
    transitionCurrentUser,
    updateUserCommPrefs,
    updateUserFavStores,
    updateUserInfo,
    saveDeliveryAddress,
    editDeliveryAddress,
    removeDeliveryAddress,
    user,
    userLoading,
    useUpdateMeMutationLoading,
    isAuthenticated,
    authTransition,
  } = _useCurrentUser();

  const {
    authLoading,
    originLocation,
    setOriginLoc,
    signIn,
    signOut,
    signUp,
    socialLogin,
    validateLogin,
    validateLoginOtp,
  } = useAccountAuthentication({
    refreshCurrentUser,
    openErrorDialog,
    transitionCurrentUser,
    setModalAuthState: handleSetModalAuthState,
    modalAuthIsOpen,
  });

  useEffect(() => {
    // save redirect url so user can get redirected correctly after login
    syncStorage.setObject(SyncStorageKey.AUTH_REDIRECT, { callbackUrl: originLocation });
  }, [originLocation]);

  const loading = authLoading || userLoading;

  const defaultCloseAuthModal = useCallback(() => {
    handleSetModalAuthState(initialModalAuthState);
  }, [handleSetModalAuthState]);

  const defaultOnSignInSuccess = useCallback(() => {
    if (modalAuthState.shouldSkipDefaultSignInCallback) {
      return;
    }
    const shouldShowToast = !!modalAuthState.shouldDisplaySignInSuccessMessage;

    if (!modalAuthState.navigateOnSuccessRoute && shouldShowToast) {
      toaster.positive(t('signInSuccess'));
    }

    if (modalAuthState.navigateOnSuccessRoute?.length) {
      if (shouldShowToast) {
        toaster.positive(t('signInSuccess'));
      }
      defaultCloseAuthModal();
      // native base modal can interfere with navigation so wait till its closed to transition
      setTimeout(() => {
        router.navigate(modalAuthState.navigateOnSuccessRoute as string);
      }, APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS);
      return;
    }
    defaultCloseAuthModal();
  }, [defaultCloseAuthModal, modalAuthState, router]);

  const authContext = {
    // Computed values
    loading,
    useUpdateMeMutationLoading,
    originLocation,
    otpAuthError,
    user: user as UserDetails,
    // Functions
    refetchCurrentUser,
    refreshCurrentUser,
    isAuthenticated,
    authTransition: {
      status: authTransition.status,
      reason: authTransition.reason,
      epoch: authTransition.epoch,
    },
    signIn,
    signOut,
    signUp,
    socialLogin,
    updateUserInfo,
    saveDeliveryAddress,
    removeDeliveryAddress,
    editDeliveryAddress,
    updateUserCommPrefs,
    updateUserFavStores,
    validateLogin,
    validateLoginOtp,
    setOriginLocation: setOriginLoc as (args: null | string) => void,
    setOtpAuthError: setOtpAuthError as (args: null | string) => void,
    setModalAuthState: handleSetModalAuthState,
    modalAuthIsOpen,
    modalAuthState,
    isReAuthenticating,
    setIsReAuthenticating,
    openSignInModal,
    onSignInSuccess: useCallback(() => {
      defaultOnSignInSuccess();
      modalAuthState?.onSignInSuccess && modalAuthState.onSignInSuccess();
    }, [defaultOnSignInSuccess, modalAuthState]),
    closeModalAuth: useCallback(() => {
      defaultCloseAuthModal();
      modalAuthState?.onClose && modalAuthState.onClose();
    }, [defaultCloseAuthModal, modalAuthState?.onClose]),
    accountCallbackUrl,
    setAccountCallbackUrl,
    alertMessage,
    setAlertMessage,
  };

  return (
    <AuthContext.Provider value={authContext}>
      {children}
      <ErrorDialog />
    </AuthContext.Provider>
  );
};

export default AuthContext.Consumer;
